import { createReducer, on } from '@ngrx/store';
import { TicketResponse, ViewOrderDetailedSecureResponse } from 'accesso-model-lib';
import { cloneDeep, get, isObject, toLower } from 'lodash-es';
import { UpdateBillingRequest } from 'src/app/pages/account/payment/payment.effects';

import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { wrapObjectToArray } from 'src/app/shared/utilities/array.utils';
import { getDateFromInput } from 'src/app/shared/utilities/date.utils';
import { toInt } from 'src/app/shared/utilities/number.utils';
import { validArray, validString } from 'src/app/shared/utilities/types.utils';
import { apiResponse, postApiRequest } from '../api/api.actions';
import { isApi } from '../api/api.utilities';
import { BookingPortalLogin } from '../api/responses/booking-portal-login';
import { Customer, PaymentPortalLogin, PhoneType, ScheduledPayments } from '../api/responses/payment-portal-login';
import {
	clearUserAction,
	getOrderFormInfoSuccess,
	guestViewOrderComplete,
	isSessionLockoutAction,
	logout,
	sessionEndedAction,
	setMfaCodeAction,
	setUserTokenAction,
	updateOrderTicketTypes,
	updateUserIdTokenAction,
	updateUTTokenAction,
} from './user.actions';
import { Details, HistoricPayment, User } from './user.models';
import { initialState, UserState } from './user.state';
import { getByIndex, getPrimary } from './user.utils';

const onUnifiedGuestLoginResponse = on(apiResponse, (state: UserState, action): UserState => {
	if (isApi(action, ApiRequestType.UNIFIED_GUEST_LOGIN)) {
		const user = new User();

		if (!action.isOk) {
			return {
				...state,
				isAuthenticating: false,
			};
		}

		user.jwt = action.response.jwt;

		return {
			...state,
			isAuthenticated: false,
			isAuthenticating: true,
			user,
		};
	}

	return state;
});

/**
 * Reducer for when we receive a BookingPortalLogin response.
 */
const onBookingPortalLoginResponse = on(apiResponse, (state: UserState, action): UserState => {
	if (isApi(action, ApiRequestType.BOOKING_PORTAL_LOGIN)) {
		let user;

		if (!action.isOk) {
			return {
				...state,
				isAuthenticating: false,
			};
		}

		// for auto login, we don't have the order ID in the params, it's in the response
		const orderId = (get(action, 'params.order_id') as string) ?? action.response.order_id;

		user = getUserFromBookingPortalResponse(action.response, orderId);
		return {
			...state,
			isAuthenticated: true,
			user: {
				...state.user,
				...user,
			},
		};
	}

	return state;
});

/**
 * Reducer for when we receive a PaymentPortalLogin response.
 */
const onPaymentPortalLoginResponse = on(apiResponse, (state: UserState, action): UserState => {
	if (isApi(action, ApiRequestType.PAYMENT_PORTAL_LOGIN)) {
		let user;

		if (!action.isOk) {
			return {
				...state,
				isAuthenticating: false,
			};
		}

		user = getUserFromPaymentPortalResponse(action.response, get(action, 'params.order_id') as string);
		return {
			...state,
			isAuthenticated: true,
			user: {
				...state.user,
				...user,
			},
		};
	}

	return state;
});

/**
 * Reducer for when we receive a GuestViewOrder response.
 */
const onGuestViewOrderResponse = on(apiResponse, (state: UserState, action): UserState => {
	if (isApi(action, ApiRequestType.GUEST_VIEW_ORDER)) {
		if (!action.isOk) {
			return {
				...state,
				isPaid: getIsPaid(action.response),
				isAuthenticating: false,
			};
		}

		const parentEConsignmentMerchantId = get(
			action.response,
			'VIEW_ORDER_REPLY.0.ORDER_STATUS_REPLY.0.parent_econsignment_merchant_id'
		) as string;

		const membershipHistory = get(
			action.response,
			'VIEW_ORDER_REPLY.0.MEMBERSHIP_DETAILS_REPLY.0.MEMBERSHIP_PAYMENTS.0.MEMBERSHIP_PAYMENT'
		) as HistoricPayment[];

		const seasonPassHistory = get(
			action.response,
			'VIEW_ORDER_REPLY.0.ORDER_STATUS_REPLY.0.SCHEDULED_PAYMENTS.0.SP'
		) as HistoricPayment[];
		let paymentHistory = membershipHistory || seasonPassHistory;
		let tickets = get(action.response, 'VIEW_ORDER_REPLY.0.TICKET_DETAILS_REPLY.0.TICKET') as TicketResponse[];
		let membershipTicketIds = [];
		const membershipInfo = get(action.response, 'VIEW_ORDER_REPLY.0.MEMBERSHIP_DETAILS_REPLY.0.MEMBERSHIP_INFO');
		let userName;

		if (validArray(membershipInfo)) {
			membershipTicketIds = membershipInfo
				.filter((membership) => isObject(membership))
				.map((membership) => {
					return membership.ticket_id;
				});
		}

		if (!Array.isArray(paymentHistory)) {
			paymentHistory = [];
		}

		if (!Array.isArray(tickets)) {
			tickets = [];
		}

		// Check if user.details exists from PPL
		// If not, it's a bookings order and need to get name info
		if (!state.user?.details) {
			userName = validArray(action.response.VIEW_ORDER_REPLY)
				? [
						getUserNameFromGuestViewOrder(
							action?.response.VIEW_ORDER_REPLY[0]?.CUSTOMER_DETAILS_REPLY[0]?.CUSTOMER[0]
						),
				  ]
				: [];
		}

		return {
			...state,
			isCancelled: getIsCancelled(action.response),
			isCancellationSet: getIsCancellationSet(action.response),
			isPaid: getIsPaid(action.response),
			isAuthenticating: false,
			user: {
				...state.user,
				membershipTicketIds,
				merchantId: get(action.response, 'VIEW_ORDER_REPLY.0.ORDER_STATUS_REPLY.0.merchant_id'),
				orderNumber: get(action.response, 'VIEW_ORDER_REPLY.0.ORDER_STATUS_REPLY.0.order_id'),
				purchaseDate: get(action.response, 'VIEW_ORDER_REPLY.0.ORDER_STATUS_REPLY.0.order_date'),
				parentEConsignmentMerchantId,
				oToken: get(action.response, 'VIEW_ORDER_REPLY.0.ORDER_STATUS_REPLY.0.oToken') as string,
				tickets,
				...(userName && { details: userName }),
				paymentHistory: paymentHistory
					.map((payment) => {
						const data = { ...payment };
						const MembershipDate = getDateFromInput(payment.scheduled_date, state.user.incomingDateFormat);
						const SeasonPassDate = getDateFromInput(payment.date, state.user.incomingDateFormat);

						// @ts-ignore:next-line
						data.amount = parseFloat(payment.amount);
						// @ts-ignore:next-line
						data.scheduled_date = MembershipDate ? MembershipDate : SeasonPassDate;
						// @ts-ignore:next-line
						data.settlement_date = getDateFromInput(payment.settlement_date, state.user.incomingDateFormat);
						data.isMembership = MembershipDate ? true : false;

						return data;
					})
					.sort((a, b) => {
						return toInt(a.payment_index) - toInt(b.payment_index);
					}),
			},
		};
	}

	return state;
});

const onGuestViewOrderComplete = on(guestViewOrderComplete, (state: UserState, action): UserState => {
	return {
		...state,
		guestViewOrderComplete: action.complete,
	};
});

/**
 * Handles cancel membership
 */
const onCancelMembership = on(apiResponse, (state: UserState, action): UserState => {
	if (isApi(action, ApiRequestType.CANCEL_MEMBERSHIP)) {
		if (!action.isOk) {
			return state;
		}

		return {
			...state,
			isCancelled: true,
		};
	}

	return state;
});

/**
 * Called when we update the billing info
 */
const onUpdateBillingInfo = on(apiResponse, (state: UserState, action): UserState => {
	if (!isApi(action, ApiRequestType.UPDATE_BILLING_INFO)) {
		return state;
	}

	const { response } = action;
	const newState = cloneDeep(state);
	const request = action.params as UpdateBillingRequest;
	const index = request.is_secondary ? 2 : 1;
	const { UPDATE } = request;
	let details = newState.user.details.find(getByIndex(index));
	let mainDetails: Partial<Details> = newState.user.details.find(getPrimary);

	if (!details) {
		details = {
			address: {
				street: UPDATE.address1,
				co_dept: UPDATE.address2 ?? '',
				city: UPDATE.city,
				state: UPDATE.state,
				country: UPDATE.country,
				zip: UPDATE.zip,
			},
			email: [
				{
					address: UPDATE.email,
					eToken: mainDetails?.email?.[0]?.eToken ?? '',
				},
			],
			phone: [
				{
					number: UPDATE.home_phone,
					pToken: mainDetails?.phone?.[0]?.pToken ?? '',
					type: PhoneType.HOME,
				},
			],
			index: '' + index,
			cToken: mainDetails?.cToken ?? '',
			first_name: mainDetails?.first_name ?? '',
			last_name: mainDetails?.last_name ?? '',
			name: mainDetails?.name ?? '',
			shipping_name: '',
		};
		newState.user.details.push(details);

		if (!mainDetails) {
			mainDetails = details;
		}
	} else {
		details.address.street = UPDATE.address1;
		details.address.co_dept = UPDATE.address2;
		details.address.city = UPDATE.city;
		details.address.state = UPDATE.state;
		details.address.country = UPDATE.country;
		details.address.zip = UPDATE.zip;
	}

	// We only want to update the main details if we're changing index 1
	if (mainDetails && index === 1) {
		if (response.eToken) {
			mainDetails.email?.forEach((email) => (email.eToken = response.eToken));
		}
		if (response.pToken) {
			mainDetails.phone?.forEach((phone) => (phone.pToken = response.pToken));
		}
	}

	const homePhone = details.phone.find((phone) => phone.type === PhoneType.HOME);
	let mobilePhone = details.phone.find((phone) => phone.type === PhoneType.MOBILE);

	homePhone.number = UPDATE.home_phone;

	if (validString(UPDATE.cell_phone)) {
		if (!mobilePhone) {
			mobilePhone = {
				number: '',
				pToken: homePhone.pToken,
				type: PhoneType.MOBILE,
			};
			details.phone.push(mobilePhone);
		}

		mobilePhone.number = UPDATE.cell_phone;
	} else if (mobilePhone) {
		details.phone.splice(details.phone.indexOf(mobilePhone), 1);
	}

	return newState;
});

/**
 * Reducer for when the we see a POST request to PaymentPortalLogin, which means they're authenticating.
 */
const isAuthenticating = on(postApiRequest, (state: UserState, action) => {
	if (!isApi(action, ApiRequestType.PAYMENT_PORTAL_LOGIN)) {
		return state;
	}

	return {
		...state,
		isAuthenticating: true,
	};
});

/**
 * Sets the MFA code to use on subsequent calls and sets the MFA expiration time
 */
const onSetMfaCode = on(setMfaCodeAction, (state: UserState, action): UserState => {
	return {
		...state,
		mfaCode: action.code,
		mfaExpiration: action.expiration,
	};
});

/**
 * Sets isSessionExpired when User session has ended
 */
const onSessionEnded = on(sessionEndedAction, (state: UserState): UserState => {
	return {
		...state,
		isSessionExpired: true,
	};
});

const onSessionLockout = on(isSessionLockoutAction, (state: UserState, action): UserState => {
	return {
		...state,
		isLockoutAccess: action.isLockout,
	};
});

/**
 * Sets the Passport Support user token to use on subsequent calls
 */
const onSetUserToken = on(setUserTokenAction, (state: UserState, action): UserState => {
	return {
		...state,
		userToken: action.userToken,
	};
});

/**
 * Saves userId encripted token passed from Passport Support for subsequent calls
 */
const onUpdateUserIdToken = on(updateUserIdTokenAction, (state: UserState, action): UserState => {
	return {
		...state,
		uidToken: action.uidToken,
	};
});

/**
 * updates ticket types on the user after getUnifiedOrderTicketType
 */
const onUpdateOrderTicketTypes = on(updateOrderTicketTypes, (state: UserState, action): UserState => {
	return {
		...state,
		orderTicketTypes: action,
	};
});

/**
 * Saves encripted user token passed from Passport Support for subsequent calls
 */
const onUpdateUTToken = on(updateUTTokenAction, (state: UserState, action): UserState => {
	return {
		...state,
		utToken: action.utToken,
	};
});

/**
 * Saves user forms and order info for contract viewer
 */
const onGetOrderFormInfoSuccess = on(getOrderFormInfoSuccess, (state: UserState, action): UserState => {
	return {
		...state,
		user: {
			...state.user,
			contractViewerDetails: action.orderFormInfo,
		},
	};
});

/**
 * Reducer for when the user logs out.
 */
const onLogout = on(logout, () => ({ ...initialState }));
const onClearUser = on(clearUserAction, () => ({ ...initialState }));

export const userReducer = createReducer(
	initialState,
	onUnifiedGuestLoginResponse,
	onPaymentPortalLoginResponse,
	onBookingPortalLoginResponse,
	onGuestViewOrderResponse,
	isAuthenticating,
	onLogout,
	onClearUser,
	onUpdateBillingInfo,
	onCancelMembership,
	onSetMfaCode,
	onSetUserToken,
	onSessionEnded,
	onSessionLockout,
	onUpdateUserIdToken,
	onUpdateUTToken,
	onUpdateOrderTicketTypes,
	onGetOrderFormInfoSuccess,
	onGuestViewOrderComplete
);

/**
 * Returns a new User object after authenticating.
 * @param response The BookingPortalLogin response.
 * @param orderId the Order ID
 * @returns A new user model
 */
export function getUserFromBookingPortalResponse(response: BookingPortalLogin, orderId: string): User {
	const user = new User();
	// No longer need to save jwt from BPL as the correct token is returned from UGL
	// purchaseDate needs to be set from GuestViewOrder as it's localized
	user.orderNumber = orderId;
	user.cToken = response.cToken;
	user.oToken = response.oToken;

	return user;
}

/**
 * Returns a new User object after authenticating.
 * @param response The payment portal login response.
 * @param orderId the order ID
 * @returns A new user model
 */
export function getUserFromPaymentPortalResponse(response: PaymentPortalLogin, orderId: string): User {
	const user = new User();

	user.currentPaymentType = response.billing_provider_desc || response.payment_type;
	user.orderNumber = orderId;
	user.status = response.VIEW_ORDER_REPLY?.MEMBERSHIP_DETAILS_REPLY?.MEMBERSHIP_QUICK_STATS?.order_status || 'N/A';
	user.scheduledPayments = validArray(response.SCHEDULED_PAYMENTS) ? response.SCHEDULED_PAYMENTS[0] : null;
	user.cards = validArray(response.CARD) ? response.CARD.slice(0) : [];
	user.cepayMerchantId = response?.cepay_merchant_id;

	if (user?.cards.length === 1) {
		const [card] = user.cards;

		if (!card.index) {
			user.cards = [
				{
					...card,
					index: '1',
				},
			];
		}
	}
	// No longer need to save jwt from PPL as the correct token is returned from UGL
	user.details = validArray(response.CUSTOMER_DETAILS_REPLY)
		? response.CUSTOMER_DETAILS_REPLY[0].CUSTOMER?.map(getAddressFromPaymentPortalResponse)
		: [];
	user.cepayMerchantId = response.cepay_merchant_id;
	user.paymentType = response.payment_type;
	user.merchantId = response.merchant_id;
	user.isDelinquent = isDelinquent(user.scheduledPayments);
	user.packageType = response?.PKG[0]?.type ?? 'N/A';
	user.compulsoryEndDate = getCompulsoryEndDate(response);
	user.remainingCompulsoryAmount = getCompulsoryAmount(response);
	user.inCompulsoryPeriod = getInCompulsoryPeriod(response);

	// This is how backend decided to do this... but we need to know which format it's coming in as...
	user.incomingDateFormat = user.packageType === 'MEM' ? 'y-m-d' : 'm-d-y';

	user.canPayoff =
		!user.isDelinquent &&
		user.packageType === 'MEM' &&
		(!isObject(user.scheduledPayments) || user.scheduledPayments.has_camp_deposit !== '1');
	user.scheduledPayments = updateScheduledPayments(user.scheduledPayments, user.incomingDateFormat);

	let schedule;
	if (validArray(response.SCHEDULED_PAYMENTS)) {
		schedule = response.SCHEDULED_PAYMENTS[0];
	}

	user.payoffAmount = schedule
		? parseFloat(schedule.remaining_compulsory_amount || schedule.remaining_balance) || 0
		: 0;
	user.delinquentAmount = schedule ? parseFloat(schedule.amount_owed) || 0 : 0;

	return user;
}

export function updateScheduledPayments(
	scheduledPayments: ScheduledPayments,
	incomingDateFormat: string
): ScheduledPayments | null {
	if (!scheduledPayments?.SP) {
		return scheduledPayments;
	}

	return {
		...scheduledPayments,
		SP: scheduledPayments.SP.map((scheduledPayment) => {
			const payment = {
				...scheduledPayment,
			};
			if (payment.date) {
				payment.date = getDateFromInput(scheduledPayment.date, incomingDateFormat);
			}
			// Declined Payments node
			if (payment.AMT) {
				payment.AMT = wrapObjectToArray(payment.AMT).map((pmt) => {
					const pymnt = {
						...pmt,
					};
					if (pymnt.date) {
						pymnt.date = getDateFromInput(pmt.date, incomingDateFormat);
					}
					return pymnt;
				});
			}
			return payment;
		}),
	};
}

export function isDelinquent(scheduledPayments: ScheduledPayments): boolean {
	if (!isObject(scheduledPayments) || !validArray(scheduledPayments.SP)) {
		return false;
	}
	return scheduledPayments.SP.some((payment) => toLower(payment.status || '').indexOf('declined') > -1);
}

export function getAddressFromPaymentPortalResponse(customer?: Customer) {
	const { cToken, first_name, id, index, last_name, name, shipping_name } = customer || {};
	return {
		address: validArray(customer.ADDRESS) ? customer.ADDRESS[0] : {},
		email: validArray(customer.EMAIL) ? customer.EMAIL : [],
		phone: validArray(customer.PHONE) ? customer.PHONE : [],
		cToken,
		first_name,
		id,
		index,
		last_name,
		name,
		shipping_name,
	};
}

export function getUserNameFromGuestViewOrder(customer?: Customer): Record<string, string> {
	const { first_name, last_name, name, id } = customer || {};
	return {
		first_name,
		last_name,
		name,
		id,
	};
}

/**
 * If the error code is 202 or 87, the user has paid off everything.
 * @param response The ViewOrder response
 * @returns If everything is paid off
 */
export function getIsPaid(response: ViewOrderDetailedSecureResponse): boolean {
	const errorCode = toInt(response?.error_code);
	return errorCode === 202 || errorCode === 87;
}

/**
 * Find out if it's a cancelled order status.
 * @param response The ViewOrder response
 * @returns If the order is cancelled
 */
export function getIsCancelled(response: ViewOrderDetailedSecureResponse): boolean {
	return (
		toLower(
			get(response, 'VIEW_ORDER_REPLY.0.MEMBERSHIP_DETAILS_REPLY.0.MEMBERSHIP_QUICK_STATS.0.order_status') || ''
		) === 'cancelled'
	);
}

/**
 * Find out if future cancellation date is set
 * @param response The api response from GuestViewOrder
 * @returns true if there is a set future date for cancellation
 */
export function getIsCancellationSet(response: ViewOrderDetailedSecureResponse): boolean {
	const { cancellation_effective_date } =
		response.VIEW_ORDER_REPLY[0]?.MEMBERSHIP_DETAILS_REPLY?.[0]?.MEMBERSHIP_INFO?.[0] || {};
	return validString(cancellation_effective_date);
}

/**
 * Returns the compulsory end date for scheduled payments
 * @param response The API response for PaymentPortalLogin
 */
export function getCompulsoryEndDate(response): Date | null {
	if (validArray(response.SCHEDULED_PAYMENTS)) {
		const [rootScheduledPayments] = response.SCHEDULED_PAYMENTS;
		const { compulsory_period_end_date } = rootScheduledPayments;

		if (validString(compulsory_period_end_date)) {
			return getDateFromInput(compulsory_period_end_date);
		}
	}
	return null;
}

/**
 * Get the remaining compulsory amount
 * @param response The PaymentPortalLogin response
 */
export function getCompulsoryAmount(response): number | null {
	if (validArray(response.SCHEDULED_PAYMENTS)) {
		const [rootScheduledPayments] = response.SCHEDULED_PAYMENTS;
		const { remaining_compulsory_amount } = rootScheduledPayments;

		if (validString(remaining_compulsory_amount)) {
			const amount = parseFloat(remaining_compulsory_amount);
			return isNaN(amount) ? null : amount;
		}
	}
	return null;
}

/**
 * Find out if we're in the compulsory period.
 * @param response The PaymentPortalLogin response
 */
export function getInCompulsoryPeriod(response): boolean {
	if (validArray(response.SCHEDULED_PAYMENTS)) {
		const [rootScheduledPayments] = response.SCHEDULED_PAYMENTS;

		return rootScheduledPayments.is_within_compulsory_period === '1';
	}
	return false;
}
